import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Form, Input, Modal } from 'antd'
import SystemHeader from '@/widgets/layout/SystemHeader'
import { BackButton, DemoBadge, DemoInput, DemoSelect, Pagination, ResultCard, SearchCard, TableLoadingRow } from '@/widgets/demo/ListPrimitives';
import { demoButtonClass, computePagination } from '@/widgets/demo/listHelpers';
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { useAuth, useColumnVisibility, useOfficeReviewStatuses, usePersistentPageSize } from '@/hooks'
import { ColumnVisibilityControl } from '@/widgets/demo/ColumnVisibilityControl'
import { recipientsApi } from '@/services/recipients/api'
import { paymentJobsApi } from '@/services/paymentJobs/api'
import { isHandledAuthError } from '@/services/client'
import type { BankInfoIssue, PaymentJobRecipientResponse } from '@/services/paymentJobs/types'
import type { RecipientResponse } from '@/services/recipients/types'
import { useToast, OfficeReviewStatusTag } from '@/widgets/common'
import BankInfoIssueDialog from '@/widgets/payment/BankInfoIssueDialog'
import { CASE_STATUS_CODE_MAP, CASE_STATUSES, caseStatusKeyByLabel } from '@/constants/enums'
import { usePermission } from '@/hooks/usePermission'
import useSearchPersistence from '@/hooks/useSearchPersistence'

const WELFARE_OPTIONS = [
  { value: '', label: '受給状態（全て）' },
  { value: '保護受給中', label: '保護受給中' },
  { value: '保護廃止中', label: '保護廃止中' },
]

// 給付額算出対象作成画面の対象は「給付額算出対象作成待」状態の受給者。
// 「給付額算出対象作成完了」ボタン押下時に SQS 送信＋給付額算出自動実行待へ遷移する。
const FIXED_PAYMENT_STATUS = CASE_STATUSES.AWAITING_TRANSFER_LIST

// 管理者のみ：他リスト紐付き済みの受給者を取り込めるよう、状態フィルタを開放する。
// 既定値は FIXED_PAYMENT_STATUS。「ステータス（全て）」は ADMIN_ALL_VALUE で表現。
const ADMIN_ALL_VALUE = '__ALL__'
const ADMIN_STATUS_KEYS = [
  'AWAITING_TRANSFER_LIST',
  'AWAITING_CALC',
  'CALC_IN_PROGRESS',
  'CALC_ERROR',
  'CALC_DONE',
  'NOTICE_PRINTED',
  'NOTICE_DOWNLOADED',
  'ZENGIN_PROD_EXPORTED',
] as const

// 自治体ごとに別キーで保存する。SYSTEM_ADMIN などで同一端末から複数自治体を行き来した際に
// 直前の自治体の文書番号が新規作成画面に流入する事故を防ぐ。
const DECISION_NOTICE_DOCUMENT_NO_STORAGE_KEY_PREFIX = 'payment-job-create.decisionNoticeDocumentNo'
const PAYMENT_JOB_NAME_MAX_LENGTH = 256

// 給付額算出対象作成（リスト作成）で一度に選択できる世帯数の上限（業務確定値: 1000世帯まで）。
// 出力（支給者CSV/通知書/振込データ）が1000世帯で制限されているため、作成時点で1000世帯を超える
// リストを作らせない（作っても出力できず問い合わせに繋がる）。世帯は識別ID（ident_id）単位で数える。
// 選択中受給者は identId をキーにした Map で保持しているため selectedRecipients.size が世帯数に一致する。
// 一覧側 PaymentJobList の MAX_SELECTABLE_HOUSEHOLDS・サーバ側 PaymentJobService.MAX_TARGET_HOUSEHOLDS と整合させること。
const MAX_TARGET_HOUSEHOLDS = 1000
const CASH_HANDOVER_CONFIRM_MESSAGE =
  '選択した世帯の中に口座情報を持たない世帯があります。これらの世帯は手渡し対応となり決定通知書のみの出力になりますが、給付額の算出を実行してもよろしいですか？'

// 列表示設定（受給者検索 No.30 と同じ仕組み）。候補一覧は列が多く横に長いため、
// ユーザーごとに不要な列を隠せるようにする。選択チェックボックス列は業務上必須なので対象外。
const COLUMN_VISIBILITY_KEY = 'payment-job-create'

const COLUMN_ORDER = [
  'identId',
  'householdIdentCode',
  'name',
  'address',
  'welfareStatus',
  'bankInfo',
  'amount',
  'pStatus',
  'officeReviewStatus',
] as const

type ColumnId = (typeof COLUMN_ORDER)[number]

const COLUMN_LABELS: Record<ColumnId, string> = {
  identId: '識別ID',
  householdIdentCode: '世帯識別符号',
  name: '氏名',
  address: '住所',
  welfareStatus: '受給状態',
  bankInfo: '口座情報',
  amount: '給付金額',
  pStatus: 'ステータス',
  officeReviewStatus: '事務局確認用ステータス',
}

// 識別ID は行（世帯）の同定に必須のため常に表示とし、外せないようにする。
const ALWAYS_VISIBLE_COLUMNS = ['identId']

// 列幅（rem）。住所のみ可変幅（余白を吸収）で、ここでの値は最低幅として min-width 計算に使う。
const COLUMN_WIDTHS_REM: Record<ColumnId, number> = {
  identId: 10.5,
  householdIdentCode: 9.5,
  name: 8.5,
  address: 12,
  welfareStatus: 9.5,
  bankInfo: 7,
  amount: 8.5,
  pStatus: 11.5,
  officeReviewStatus: 11,
}

const SELECT_COLUMN_WIDTH_REM = 4

// 事務局確認用ステータス絞り込み（No.25 と同じ値の流儀）。ALL=絞り込まない / '0'=未設定 / master.id。
const OFFICE_REVIEW_ALL_VALUE = 'ALL'
const OFFICE_REVIEW_UNSET_VALUE = '0'

/** 選択値 → サーバ送信値。ALL=undefined / '0'=0(未設定) / id=数値。 */
function officeReviewParam(value: string | undefined): number | undefined {
  if (!value || value === OFFICE_REVIEW_ALL_VALUE) return undefined
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const COLUMN_VISIBILITY_ITEMS = COLUMN_ORDER.map((id) => ({ id, label: COLUMN_LABELS[id] }))

type CompletionFormValues = {
  jobName: string
  transferDate: string
  decisionNoticeDocumentNo?: string
  /** 決定通知書発行日（任意, yyyy-MM-dd）。未入力なら決定通知書には算出実行日が印字される。 */
  decisionNoticeIssueDate?: string
  /** 支給日支給方法（任意）。未入力なら口座情報と振込指定日から自動生成される。 */
  decisionNoticePaymentDateMethod?: string
}

function storageKeyFor(municipalityId: number | null): string {
  return `${DECISION_NOTICE_DOCUMENT_NO_STORAGE_KEY_PREFIX}:${municipalityId ?? 'none'}`
}

function readSavedDecisionNoticeDocumentNo(municipalityId: number | null): string {
  try {
    if (typeof window === 'undefined') return ''
    return window.localStorage.getItem(storageKeyFor(municipalityId)) ?? ''
  } catch {
    return ''
  }
}

function saveDecisionNoticeDocumentNo(municipalityId: number | null, value: string): void {
  try {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(storageKeyFor(municipalityId), value)
  } catch {
    // localStorage is best effort.
  }
}

function normalizeDecisionNoticeDocumentNo(value: string | null | undefined): string {
  return (value ?? '').trim()
}

function parsePositiveInt(value: string | null): number | null {
  if (!value) return null
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
}

interface RecipientRow {
  key: number
  id: number
  identId: string
  householdIdentCode: string
  name: string
  address: string
  welfareStatus: string
  benefitAmount: number | null
  paymentState: string
  bankInfoFilled: boolean
  /** 事務局確認用ステータスID（null=未設定「—」）。表示名・色はマスタと突合（顧客要望 管理No.50） */
  officeReviewStatusId: number | null
}

interface SelectedRecipient {
  key: string
  id: number
  identId: string
  householdIdentCode: string
  name: string
  address: string
  welfareStatus: string
  paymentState: string
  amount: number
  kana: string
  pinned: boolean
  bankInfoFilled: boolean
  officeReviewStatusId: number | null
}

const welfareColor = (status: string) => {
  if (status.includes('受給')) return 'bg-green-100 text-green-700'
  if (status.includes('停止')) return 'bg-yellow-100 text-yellow-700'
  if (status.includes('廃止')) return 'bg-gray-100 text-gray-700'
  return 'bg-gray-100 text-gray-700'
}


function toRecipientRow(recipient: RecipientResponse): RecipientRow {
  return {
    key: recipient.id,
    id: recipient.id,
    identId: recipient.identId,
    householdIdentCode: recipient.householdIdentCode,
    name: recipient.name ?? '',
    address: recipient.address ?? '',
    welfareStatus: recipient.welfareStatus ?? '',
    benefitAmount: recipient.benefitAmount ?? null,
    paymentState: recipient.paymentState ?? '',
    bankInfoFilled: Boolean(recipient.bankInfoFilled),
    officeReviewStatusId: recipient.officeReviewStatusId ?? null,
  }
}

function toSelectedRecipient(row: RecipientRow): SelectedRecipient {
  return {
    key: String(row.id),
    id: row.id,
    identId: row.identId,
    householdIdentCode: row.householdIdentCode,
    name: row.name,
    address: row.address,
    welfareStatus: row.welfareStatus,
    paymentState: row.paymentState || FIXED_PAYMENT_STATUS,
    amount: row.benefitAmount ?? 0,
    kana: '',
    pinned: false,
    bankInfoFilled: row.bankInfoFilled,
    officeReviewStatusId: row.officeReviewStatusId,
  }
}

// 受給者検索（候補一覧）の最後に適用した絞り込み条件とページを localStorage に保持する
// （顧客要望：「検索条件は最後に選択した結果を保持する」）。
// 作成/編集フローの一時状態（選択済み受給者・リスト名・振込指定日・文書番号など）は保持しない。
// 検索条件はジョブ初期化（loadJob）と独立しているため、編集モードを破壊する恐れはない。
// 自治体や create/edit を跨いで同じ検索パネルを共有するため、キーは単一の 'payment-job-create' とする。
interface PaymentJobCreateSearch {
  identId: string
  name: string
  householdIdentCode: string
  address: string
  welfareStatus: string
  paymentStatus: string
  paymentStatuses: string
  bankInfoFilled: '' | 'true' | 'false'
  /** 事務局確認用ステータス絞り込み（No.25）。ALL / '0'=未設定 / master.id。 */
  officeReviewUi: string
  page: number
}

const DEFAULT_SEARCH: PaymentJobCreateSearch = {
  identId: '',
  name: '',
  householdIdentCode: '',
  address: '',
  welfareStatus: '',
  paymentStatus: FIXED_PAYMENT_STATUS,
  paymentStatuses: '',
  bankInfoFilled: '',
  officeReviewUi: OFFICE_REVIEW_ALL_VALUE,
  page: 1,
}

function toSelectedRecipientFromJob(recipient: PaymentJobRecipientResponse): SelectedRecipient {
  return {
    key: `job-${recipient.id}`,
    id: recipient.id,
    identId: recipient.identId,
    householdIdentCode: recipient.householdNo ?? '',
    name: recipient.name ?? '',
    address: '—',
    welfareStatus: '',
    paymentState: FIXED_PAYMENT_STATUS,
    amount: recipient.amount ?? 0,
    kana: recipient.kana ?? '',
    pinned: true,
    bankInfoFilled: Boolean(recipient.bankInfoFilled),
    officeReviewStatusId: recipient.officeReviewStatusId ?? null,
  }
}

const PaymentJobCreate = () => {
  const navigate = useAppNavigate()
  const toast = useToast()
  const { jobId: jobIdParam } = useParams<{ jobId?: string }>()
  const jobId = parsePositiveInt(jobIdParam ?? null)
  const isEdit = jobId != null
  const selectAllRef = useRef<HTMLInputElement>(null)

  const { isAdmin } = usePermission()
  const { session } = useAuth()
  const municipalityId = session?.municipalityId ?? null

  const [savedSearch, setSavedSearch] = useSearchPersistence<PaymentJobCreateSearch>('payment-job-create', DEFAULT_SEARCH)

  const [identId, setIdentId] = useState(savedSearch.identId)
  const [nameFilter, setNameFilter] = useState(savedSearch.name)
  const [householdCode, setHouseholdCode] = useState(savedSearch.householdIdentCode)
  const [addressFilter, setAddressFilter] = useState(savedSearch.address)
  const [welfareStatus, setWelfareStatus] = useState(savedSearch.welfareStatus)
  // '' = 全て / 'true' = 入力済み / 'false' = 未入力
  const [bankInfoFilledFilter, setBankInfoFilledFilter] = useState<'' | 'true' | 'false'>(savedSearch.bankInfoFilled)
  // 事務局確認用ステータス絞り込み。保存値が無い（旧バージョンの保存内容）場合は「全て」。
  const [officeReviewUi, setOfficeReviewUi] = useState<string>(savedSearch.officeReviewUi || OFFICE_REVIEW_ALL_VALUE)
  const { statuses: officeReviewStatuses } = useOfficeReviewStatuses()
  // 管理者用の状態フィルタ。既定は FIXED_PAYMENT_STATUS。'__ALL__' は ADMIN_STATUS_KEYS の全件 IN 検索。
  // 永続化された applied（paymentStatus / paymentStatuses）からドラフトの選択値を復元する。
  const [paymentStatusValue, setPaymentStatusValue] = useState<string>(
    savedSearch.paymentStatuses ? ADMIN_ALL_VALUE : (savedSearch.paymentStatus || FIXED_PAYMENT_STATUS),
  )

  const adminStatusOptions = useMemo(
    () => ADMIN_STATUS_KEYS.map((key) => ({ value: CASE_STATUSES[key], label: CASE_STATUSES[key] })),
    [],
  )

  const [rows, setRows] = useState<RecipientRow[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(savedSearch.page ?? 1)
  const [pageSize, setPageSize] = usePersistentPageSize('payment-job-create')
  const [loading, setLoading] = useState(false)
  const [jobLoading, setJobLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [selectedRecipients, setSelectedRecipients] = useState<Map<string, SelectedRecipient>>(new Map())
  const [modalOpen, setModalOpen] = useState(false)
  const [jobNameValue, setJobNameValue] = useState('')
  const [transferDateValue, setTransferDateValue] = useState('')
  const [decisionNoticeDocumentNoValue, setDecisionNoticeDocumentNoValue] = useState(() =>
    readSavedDecisionNoticeDocumentNo(municipalityId),
  )
  // 決定通知書発行日（任意・顧客要望 管理No.51）。文書番号と違い自治体ごとの前回値は保持せず、
  // 毎回明示的に入力させる（起案日は都度違うため、前回値の流用は誤記の元になる）。
  const [decisionNoticeIssueDateValue, setDecisionNoticeIssueDateValue] = useState('')
  // 支給日支給方法（任意）。未入力なら口座情報と振込指定日から自動生成する。
  // 発行日と同じく前回値は保持しない（リストごとに文言が違うため）。
  const [decisionNoticePaymentDateMethodValue, setDecisionNoticePaymentDateMethodValue] = useState('')
  const [completionForm] = Form.useForm<CompletionFormValues>()
  const [bankIssues, setBankIssues] = useState<BankInfoIssue[]>([])
  void setBankIssues // 事前チェック無効化中は未呼び出し。再有効化時にこの行を削除する。
  const [bankIssueDialogOpen, setBankIssueDialogOpen] = useState(false)

  const [applied, setApplied] = useState({
    identId: savedSearch.identId,
    name: savedSearch.name,
    householdIdentCode: savedSearch.householdIdentCode,
    address: savedSearch.address,
    welfareStatus: savedSearch.welfareStatus,
    paymentStatus: savedSearch.paymentStatus,
    paymentStatuses: savedSearch.paymentStatuses as string,
    bankInfoFilled: savedSearch.bankInfoFilled as '' | 'true' | 'false',
    officeReviewUi: savedSearch.officeReviewUi || OFFICE_REVIEW_ALL_VALUE,
  })

  const {
    isVisible: isColVisible,
    setVisible: setColVisible,
    reset: resetCols,
    hiddenCount: hiddenColCount,
    visibleIds: visibleColumnIds,
  } = useColumnVisibility(COLUMN_VISIBILITY_KEY, COLUMN_ORDER, { alwaysVisible: ALWAYS_VISIBLE_COLUMNS })

  // 選択チェックボックス列 + 表示中の列。空表示行の colSpan と、横スクロール用の最低幅に使う。
  const tableColSpan = 1 + visibleColumnIds.length
  const tableMinWidthRem =
    SELECT_COLUMN_WIDTH_REM +
    visibleColumnIds.reduce((sum, id) => sum + COLUMN_WIDTHS_REM[id as ColumnId], 0)

  const { totalPages, safePage, rangeFrom, rangeTo } = computePagination(page, total, pageSize)

  const fetchPage = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await recipientsApi.list({
        identId: applied.identId || undefined,
        name: applied.name || undefined,
        householdIdentCode: applied.householdIdentCode || undefined,
        address: applied.address || undefined,
        welfareStatus: applied.welfareStatus || undefined,
        paymentStatus: applied.paymentStatuses ? undefined : applied.paymentStatus || undefined,
        paymentStatuses: applied.paymentStatuses || undefined,
        bankInfoFilled: applied.bankInfoFilled === '' ? undefined : applied.bankInfoFilled === 'true',
        officeReviewStatusId: officeReviewParam(applied.officeReviewUi),
        page: safePage - 1,
        size: pageSize,
        excludeDeleted: true,
      })
      setRows(data.content.map(toRecipientRow))
      setTotal(data.totalElements)
    } catch (error) {
      if (isHandledAuthError(error)) return
      setRows([])
      setTotal(0)
    } finally {
      setLoading(false)
    }
  }, [applied, safePage, pageSize])

  useEffect(() => {
    // 検索条件・ページ変更に応じて候補一覧を取得する正当なデータ取得 effect。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchPage()
  }, [fetchPage])

  useEffect(() => {
    // 総件数減少で範囲外になったページを有効ページへ寄せる補正。
    if (page !== safePage) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPage(safePage)
    }
  }, [page, safePage])

  useEffect(() => {
    if (rows.length === 0) {
      return
    }

    // 取得結果に合わせて選択中受給者の表示情報を同期する。
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedRecipients((prev) => {
      const next = new Map(prev)
      rows.forEach((row) => {
        const existing = next.get(row.identId)
        if (!existing) return
        next.set(row.identId, {
          ...existing,
          key: String(row.id),
          id: row.id,
          householdIdentCode: row.householdIdentCode || existing.householdIdentCode,
          name: row.name || existing.name,
          address: row.address || existing.address,
          welfareStatus: row.welfareStatus || existing.welfareStatus,
          paymentState: row.paymentState || existing.paymentState,
          amount: row.benefitAmount ?? existing.amount,
          bankInfoFilled: row.bankInfoFilled,
          officeReviewStatusId: row.officeReviewStatusId,
          pinned: false,
        })
      })
      return next
    })
  }, [rows])

  useEffect(() => {
    let cancelled = false

    const loadJob = async () => {
      if (!jobId) {
        setJobNameValue('')
        setTransferDateValue('')
        setDecisionNoticePaymentDateMethodValue('')
        setSelectedRecipients(new Map())
        const savedDocumentNo = readSavedDecisionNoticeDocumentNo(municipalityId)
        setDecisionNoticeDocumentNoValue(savedDocumentNo)
        try {
          const { data } = await paymentJobsApi.list({
            page: 0,
            size: 1,
            sortBy: 'createdAt',
            order: 'DESC',
            excludeDeleted: true,
          })
          if (cancelled) return
          const latestDocumentNo = normalizeDecisionNoticeDocumentNo(data.content?.[0]?.decisionNoticeDocumentNo)
          setDecisionNoticeDocumentNoValue(latestDocumentNo)
          saveDecisionNoticeDocumentNo(municipalityId, latestDocumentNo)
        } catch (error) {
          if (isHandledAuthError(error)) return
        }
        return
      }

      setJobLoading(true)
      try {
        const { data } = await paymentJobsApi.getById(jobId)
        if (cancelled) return
        setJobNameValue(data.jobName ?? '')
        setTransferDateValue(data.transferDate ?? '')
        setDecisionNoticeDocumentNoValue(normalizeDecisionNoticeDocumentNo(data.decisionNoticeDocumentNo))
        setDecisionNoticeIssueDateValue(data.decisionNoticeIssueDate ?? '')
        setDecisionNoticePaymentDateMethodValue(data.decisionNoticePaymentDateMethod ?? '')

        const next = new Map<string, SelectedRecipient>()
        ;(data.recipients ?? []).forEach((recipient) => {
          next.set(recipient.identId, toSelectedRecipientFromJob(recipient))
        })
        setSelectedRecipients(next)
      } catch (error) {
        if (isHandledAuthError(error)) return
        navigate('payment-job')
      } finally {
        if (!cancelled) {
          setJobLoading(false)
        }
      }
    }

    void loadJob()
    return () => {
      cancelled = true
    }
  }, [jobId, navigate, toast, municipalityId])

  const resolvePaymentFilter = (value: string) => {
    if (isAdmin && value === ADMIN_ALL_VALUE) {
      return { paymentStatus: '', paymentStatuses: ADMIN_STATUS_KEYS.map((k) => CASE_STATUSES[k]).join(',') }
    }
    return { paymentStatus: value || FIXED_PAYMENT_STATUS, paymentStatuses: '' }
  }

  const runSearch = () => {
    setLoading(true)
    const filter = resolvePaymentFilter(paymentStatusValue)
    const nextSearch: PaymentJobCreateSearch = {
      identId: identId.trim(),
      name: nameFilter.trim(),
      householdIdentCode: householdCode.trim(),
      address: addressFilter.trim(),
      welfareStatus,
      paymentStatus: filter.paymentStatus,
      paymentStatuses: filter.paymentStatuses,
      bankInfoFilled: bankInfoFilledFilter,
      officeReviewUi,
      page: 1,
    }
    setApplied(nextSearch)
    setPage(1)
    setSavedSearch(nextSearch)
  }

  const runClear = () => {
    setLoading(true)
    setIdentId('')
    setNameFilter('')
    setHouseholdCode('')
    setAddressFilter('')
    setWelfareStatus('')
    setPaymentStatusValue(FIXED_PAYMENT_STATUS)
    setBankInfoFilledFilter('')
    setOfficeReviewUi(OFFICE_REVIEW_ALL_VALUE)
    const clearedSearch: PaymentJobCreateSearch = {
      identId: '',
      name: '',
      householdIdentCode: '',
      address: '',
      welfareStatus: '',
      paymentStatus: FIXED_PAYMENT_STATUS,
      paymentStatuses: '',
      bankInfoFilled: '',
      officeReviewUi: OFFICE_REVIEW_ALL_VALUE,
      page: 1,
    }
    setApplied(clearedSearch)
    setPage(1)
    setSavedSearch(clearedSearch)
  }

  // 選択世帯数が上限を超える場合に警告ポップアップ（モーダル）を表示する。
  // 顧客要望により、この1000世帯制限はトースト等ではなくポップアップで明示する。
  const warnHouseholdLimitExceeded = () => {
    Modal.error({
      content: '選択対象が1,000世帯を超えているため、選択できません。',
    })
  }

  const toggleSearchRow = (row: RecipientRow) => {
    // 選択解除は常に許可。新規選択時のみ上限（世帯数 = identId 単位）をチェックする。
    if (!selectedRecipients.has(row.identId) && selectedRecipients.size >= MAX_TARGET_HOUSEHOLDS) {
      warnHouseholdLimitExceeded()
      return
    }
    setSelectedRecipients((prev) => {
      const next = new Map(prev)
      if (next.has(row.identId)) {
        next.delete(row.identId)
      } else {
        next.set(row.identId, toSelectedRecipient(row))
      }
      return next
    })
  }

  const allSearchRowsSelected = rows.length > 0 && rows.every((row) => selectedRecipients.has(row.identId))
  const someSearchRowsSelected = rows.some((row) => selectedRecipients.has(row.identId))

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSearchRowsSelected && !allSearchRowsSelected
    }
  }, [allSearchRowsSelected, someSearchRowsSelected])

  const toggleSelectAllSearchRows = (checked: boolean) => {
    if (!checked) {
      // 本ページ全解除は無条件で許可。
      setSelectedRecipients((prev) => {
        const next = new Map(prev)
        rows.forEach((row) => next.delete(row.identId))
        return next
      })
      return
    }

    // 本ページ全選択。世帯数（identId 単位）が上限を超える分は追加しない。
    // 追加しきれなかった行があれば警告ポップアップを表示する。
    let skipped = false
    setSelectedRecipients((prev) => {
      const next = new Map(prev)
      rows.forEach((row) => {
        if (next.has(row.identId)) return
        if (next.size >= MAX_TARGET_HOUSEHOLDS) {
          skipped = true
          return
        }
        next.set(row.identId, toSelectedRecipient(row))
      })
      if (skipped) {
        warnHouseholdLimitExceeded()
      }
      return next
    })
  }

  const pinnedRows = useMemo(
    () =>
      Array.from(selectedRecipients.values()).filter(
        (recipient) => !rows.some((row) => row.identId === recipient.identId),
      ),
    [rows, selectedRecipients],
  )

  const showCompletionModal = () => {
    completionForm.setFieldsValue({
      jobName: jobNameValue,
      transferDate: transferDateValue,
      decisionNoticeDocumentNo: decisionNoticeDocumentNoValue,
      decisionNoticeIssueDate: decisionNoticeIssueDateValue,
      decisionNoticePaymentDateMethod: decisionNoticePaymentDateMethodValue,
    })
    setModalOpen(true)
  }

  const openCompletionModal = () => {
    if (selectedRecipients.size === 0) {
      toast.warning('対象者を1件以上選択してください。')
      return
    }

    // 送信時の最終ガード（選択時チェックの保険）。世帯数（identId 単位）が上限を超える場合は
    // ポップアップで明示し、作成を中止する。
    if (selectedRecipients.size > MAX_TARGET_HOUSEHOLDS) {
      warnHouseholdLimitExceeded()
      return
    }

    const hasRecipientWithoutBankInfo = Array.from(selectedRecipients.values()).some(
      (recipient) => !recipient.bankInfoFilled,
    )
    if (selectedRecipients.size >= 2 && hasRecipientWithoutBankInfo) {
      Modal.confirm({
        title: '確認',
        content: CASH_HANDOVER_CONFIRM_MESSAGE,
        okText: '続行',
        cancelText: 'キャンセル',
        onOk: showCompletionModal,
      })
      return
    }

    showCompletionModal()
  }

  const closeCompletionModal = () => {
    if (saving) return
    setModalOpen(false)
    completionForm.resetFields()
  }

  const savePaymentJob = async (values: CompletionFormValues) => {
    const jobName = values.jobName.trim()
    const transferDate = values.transferDate
    const decisionNoticeDocumentNo = normalizeDecisionNoticeDocumentNo(values.decisionNoticeDocumentNo)
    // 空文字（未入力）は送らない。バックエンドは null のとき従来どおり算出実行日を印字する。
    const decisionNoticeIssueDate = values.decisionNoticeIssueDate?.trim() || ''
    const decisionNoticePaymentDateMethod = values.decisionNoticePaymentDateMethod?.trim() || ''

    setSaving(true)
    try {
      // 受給者口座情報（FY2603〜FY2607）の事前チェック。
      // 1 件でも不備があれば作成を中止し、詳細モーダルを表示する。
      // ※ 一時的に無効化（2026-05-14）。要件確定後に再有効化する。
      // try {
      //   const identIds = Array.from(selectedRecipients.values())
      //     .map((r) => r.identId)
      //     .filter((id): id is string => Boolean(id))
      //   if (identIds.length > 0) {
      //     const { data: validation } = await paymentJobsApi.validateBankInfo({ identIds })
      //     if (validation.issues.length > 0) {
      //       setBankIssues(validation.issues)
      //       setBankIssueDialogOpen(true)
      //       return
      //     }
      //   }
      // } catch (error) {
      //   if (isHandledAuthError(error)) return
      //   toast.warning('受給者口座情報の事前チェックに失敗したため、作成を中止しました。')
      //   return
      // }

      const recipients = Array.from(selectedRecipients.values()).map((recipient) => ({
        identId: recipient.identId,
        householdNo: recipient.householdIdentCode || undefined,
        name: recipient.name || undefined,
        kana: recipient.kana || undefined,
        amount: recipient.amount ?? 0,
      }))

      if (isEdit && jobId) {
        // 編集時は振込指定日・決定通知書文書番号を変更不可。バックエンドも当該2項目を無視するが、
        // UI の disabled だけに依存せず、フロントからもこれらを送信しない（送信ペイロードからも除外）。
        await paymentJobsApi.update(jobId, { jobName, recipients })
        toast.success('給付額算出対象を更新しました。')
      } else {
        await paymentJobsApi.create({
          jobName,
          transferDate,
          ...(decisionNoticeDocumentNo ? { decisionNoticeDocumentNo } : {}),
          ...(decisionNoticeIssueDate ? { decisionNoticeIssueDate } : {}),
          ...(decisionNoticePaymentDateMethod ? { decisionNoticePaymentDateMethod } : {}),
          recipients,
        })
        saveDecisionNoticeDocumentNo(municipalityId, decisionNoticeDocumentNo)
        setDecisionNoticeDocumentNoValue(decisionNoticeDocumentNo)
        setDecisionNoticeIssueDateValue(decisionNoticeIssueDate)
        setDecisionNoticePaymentDateMethodValue(decisionNoticePaymentDateMethod)
        toast.success('給付額算出対象の作成を開始しました。一覧で進捗をご確認ください。')
      }

      setJobNameValue(jobName)
      setTransferDateValue(transferDate)
      if (isEdit) {
        setDecisionNoticeDocumentNoValue(decisionNoticeDocumentNo)
      }
      setModalOpen(false)
      completionForm.resetFields()
      navigate('payment-job')
    } catch (error) {
      if (isHandledAuthError(error)) return
    } finally {
      setSaving(false)
    }
  }

  const handleConfirmSave = async () => {
    if (selectedRecipients.size === 0) {
      toast.warning('対象者を1件以上選択してください。')
      return
    }

    let values: CompletionFormValues
    try {
      values = await completionForm.validateFields()
    } catch {
      return
    }

    await savePaymentJob(values)
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader
        breadcrumbs={[
          { label: '支給者管理', page: 'payment-job' },
          { label: isEdit ? '給付額算出対象編集' : '給付額算出対象作成' },
        ]}
      />
      <main className="mx-auto max-w-7xl px-6 py-8">
        <BackButton onClick={() => navigate('payment-job')} />

        <SearchCard className="mb-6">
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <DemoInput
              type="text"
              placeholder="識別ID"
              value={identId}
              onChange={(event) => setIdentId(event.target.value)}
            />
            <DemoInput
              type="text"
              placeholder="氏名"
              value={nameFilter}
              onChange={(event) => setNameFilter(event.target.value)}
            />
            <DemoInput
              type="text"
              placeholder="世帯識別符号"
              value={householdCode}
              onChange={(event) => setHouseholdCode(event.target.value)}
            />
            <DemoInput
              type="text"
              placeholder="住所"
              value={addressFilter}
              onChange={(event) => setAddressFilter(event.target.value)}
            />
            <DemoSelect
              value={welfareStatus}
              onChange={(event) => setWelfareStatus(event.target.value)}
            >
              {WELFARE_OPTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </DemoSelect>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <DemoSelect
                value={paymentStatusValue}
                disabled={!isAdmin}
                aria-label="ステータス"
                className={isAdmin ? 'w-56' : 'w-56 bg-gray-50 text-gray-700'}
                onChange={(event) => setPaymentStatusValue(event.target.value)}
              >
                <option value={FIXED_PAYMENT_STATUS}>{FIXED_PAYMENT_STATUS}</option>
                {isAdmin && (
                  <>
                    <option value={ADMIN_ALL_VALUE}>ステータス（全て）</option>
                    {adminStatusOptions
                      .filter((opt) => opt.value !== FIXED_PAYMENT_STATUS)
                      .map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                  </>
                )}
              </DemoSelect>
              <DemoSelect
                aria-label="口座情報"
                value={bankInfoFilledFilter}
                onChange={(event) => setBankInfoFilledFilter(event.target.value as '' | 'true' | 'false')}
                className="w-56"
              >
                <option value="">口座情報（全て）</option>
                <option value="true">有</option>
                <option value="false">無</option>
              </DemoSelect>
              <DemoSelect
                aria-label="事務局確認用ステータス"
                value={officeReviewUi}
                onChange={(event) => setOfficeReviewUi(event.target.value)}
                className="w-64"
              >
                <option value={OFFICE_REVIEW_ALL_VALUE}>事務局確認用ステータス（全て）</option>
                <option value={OFFICE_REVIEW_UNSET_VALUE}>事務局確認用ステータス：未設定</option>
                {officeReviewStatuses.map((status) => (
                  <option key={status.id} value={String(status.id)}>
                    {status.displayName}
                  </option>
                ))}
              </DemoSelect>
            </div>

            <div className="flex gap-3">
              <button type="button" onClick={runClear} className={demoButtonClass.secondary}>
                クリア
              </button>
              <button type="button" onClick={runSearch} className={demoButtonClass.primary}>
                検索
              </button>
            </div>
          </div>
        </SearchCard>

        <ResultCard
          title={(
            <div>
              <h2 className="text-xl font-bold text-gray-800">検索結果（{total}件）</h2>
              <p className="mt-1 text-sm text-gray-500">選択中: {selectedRecipients.size}件</p>
            </div>
          )}
          extra={(
            <>
            <ColumnVisibilityControl
              columns={COLUMN_VISIBILITY_ITEMS}
              isVisible={isColVisible}
              setVisible={setColVisible}
              onReset={resetCols}
              alwaysVisible={ALWAYS_VISIBLE_COLUMNS}
              hiddenCount={hiddenColCount}
            />
            <button
              type="button"
              onClick={openCompletionModal}
              disabled={jobLoading || saving}
              className={demoButtonClass.orange}
            >
              {isEdit ? '給付額算出対象更新' : '給付額算出対象作成'}
            </button>
            </>
          )}
        >
          {pinnedRows.length > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              現在の検索結果に含まれない選択済み対象者を {pinnedRows.length} 件表示しています。チェックを外すと候補から除外できます。
            </div>
          )}

          {/* 列幅の合計が画面幅を超える場合は横スクロールさせる（overflow-hidden だと最終列が切れる）。 */}
          <div className="overflow-x-auto">
            {/* 最低幅は「選択列 + 表示中の列幅の合計」。住所は余白を吸収する可変幅。 */}
            <table className="w-full table-fixed" style={{ minWidth: `${tableMinWidthRem}rem` }}>
              <colgroup>
                <col style={{ width: `${SELECT_COLUMN_WIDTH_REM}rem` }} />
                {visibleColumnIds.map((id) => (
                  <col
                    key={id}
                    style={id === 'address' ? undefined : { width: `${COLUMN_WIDTHS_REM[id as ColumnId]}rem` }}
                  />
                ))}
              </colgroup>
              <thead className="border-b-2 border-gray-200 bg-gray-50">
                <tr>
                  <th className="w-12 border-r border-gray-100 px-3 py-3 text-center text-sm font-medium text-gray-700">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allSearchRowsSelected}
                      onChange={(event) => toggleSelectAllSearchRows(event.target.checked)}
                      disabled={rows.length === 0}
                      className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </th>
                  {isColVisible('identId') && (
                    <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-700">識別ID</th>
                  )}
                  {isColVisible('householdIdentCode') && (
                    <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-700">世帯識別符号</th>
                  )}
                  {isColVisible('name') && (
                    <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-700">氏名</th>
                  )}
                  {isColVisible('address') && (
                    <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-700">住所</th>
                  )}
                  {isColVisible('welfareStatus') && (
                    <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-700">受給状態</th>
                  )}
                  {isColVisible('bankInfo') && (
                    <th className="whitespace-nowrap px-4 py-3 text-center text-sm font-medium text-gray-700">口座情報</th>
                  )}
                  {isColVisible('amount') && (
                    <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-700">給付金額</th>
                  )}
                  {isColVisible('pStatus') && (
                    <th className="whitespace-nowrap px-4 py-3 text-left text-sm font-medium text-gray-700">ステータス</th>
                  )}
                  {isColVisible('officeReviewStatus') && (
                    <th className="px-4 py-3 text-left text-sm font-medium leading-tight text-gray-700">事務局確認用ステータス</th>
                  )}
                </tr>
              </thead>

              <tbody className={`divide-y divide-gray-200 transition-opacity ${loading || jobLoading ? 'opacity-60' : ''}`}>
                {pinnedRows.length === 0 && rows.length === 0 ? (
                  loading || jobLoading ? (
                    <TableLoadingRow colSpan={tableColSpan} />
                  ) : (
                    <tr>
                      <td colSpan={tableColSpan} className="px-4 py-8 text-center text-sm text-gray-500">
                        該当するデータがありません
                      </td>
                    </tr>
                  )
                ) : (
                  <>
                    {pinnedRows.map((row) => (
                      <tr key={row.key} className="bg-amber-50/60 transition hover:bg-amber-100/60">
                        <td className="w-12 border-r border-gray-100 px-3 py-3 text-center align-top">
                          <input
                            type="checkbox"
                            checked
                            onChange={() => {
                              setSelectedRecipients((prev) => {
                                const next = new Map(prev)
                                next.delete(row.identId)
                                return next
                              })
                            }}
                            className="mt-1 h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        {isColVisible('identId') && (
                          <td className="px-4 py-3 text-sm font-mono font-medium">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="truncate" title={row.identId}>{row.identId}</span>
                              {/* 「選択済」は通常は世帯識別符号側に出す。その列を隠している場合だけここに出す。 */}
                              {!isColVisible('householdIdentCode') && (
                                <span className="inline-flex shrink-0 whitespace-nowrap rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-900">
                                  選択済
                                </span>
                              )}
                            </div>
                          </td>
                        )}
                        {isColVisible('householdIdentCode') && (
                          <td className="px-4 py-3 text-sm font-bold">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="truncate" title={row.householdIdentCode || '—'}>{row.householdIdentCode || '—'}</span>
                              <span className="inline-flex shrink-0 whitespace-nowrap rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-medium text-amber-900">
                                選択済
                              </span>
                            </div>
                          </td>
                        )}
                        {isColVisible('name') && (
                          <td className="px-4 py-3 text-sm">
                            <div className="truncate" title={row.name || '—'}>{row.name || '—'}</div>
                          </td>
                        )}
                        {isColVisible('address') && (
                          <td className="px-4 py-3 text-sm text-gray-700">
                            <div className="truncate" title={row.address || '—'}>{row.address || '—'}</div>
                          </td>
                        )}
                        {isColVisible('welfareStatus') && (
                          <td className="px-4 py-3">
                            {row.welfareStatus ? (
                              <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-1 text-xs ${welfareColor(row.welfareStatus)}`}>
                                {row.welfareStatus}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                        )}
                        {isColVisible('bankInfo') && (
                          <td className="px-4 py-3 text-center text-sm">
                            {row.bankInfoFilled ? (
                              <span className="font-medium text-emerald-700">有</span>
                            ) : (
                              <span className="text-gray-500">無</span>
                            )}
                          </td>
                        )}
                        {isColVisible('amount') && (
                          <td className="whitespace-nowrap px-4 py-3 text-sm">{(() => {
                            const key = caseStatusKeyByLabel(row.paymentState)
                            const calculated = key != null && CASE_STATUS_CODE_MAP[key] >= CASE_STATUS_CODE_MAP.CALC_DONE
                            if (!calculated || row.amount == null) return 'ー'
                            return `${row.amount.toLocaleString()}円`
                          })()}</td>
                        )}
                        {isColVisible('pStatus') && (
                          <td className="px-4 py-3">
                            <DemoBadge status={row.paymentState} />
                          </td>
                        )}
                        {isColVisible('officeReviewStatus') && (
                          <td className="px-4 py-3">
                            <OfficeReviewStatusTag statusId={row.officeReviewStatusId} />
                          </td>
                        )}
                      </tr>
                    ))}

                    {rows.map((row) => {
                      const selected = selectedRecipients.get(row.identId)
                      const amount = selected?.amount ?? row.benefitAmount ?? null
                      const currentPaymentState = selected?.paymentState || row.paymentState || FIXED_PAYMENT_STATUS

                      return (
                        <tr key={row.key} className="transition hover:bg-blue-50">
                          <td className="w-12 border-r border-gray-100 px-3 py-3 text-center align-top">
                            <input
                              type="checkbox"
                              checked={Boolean(selected)}
                              onChange={() => toggleSearchRow(row)}
                              className="mt-1 h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </td>
                          {isColVisible('identId') && (
                            <td className="px-4 py-3 text-sm font-mono font-medium">
                              <div className="truncate" title={row.identId}>{row.identId}</div>
                            </td>
                          )}
                          {isColVisible('householdIdentCode') && (
                            <td className="px-4 py-3 text-sm font-bold">
                              <div className="truncate" title={row.householdIdentCode}>{row.householdIdentCode}</div>
                            </td>
                          )}
                          {isColVisible('name') && (
                            <td className="px-4 py-3 text-sm">
                              <div className="truncate" title={row.name}>{row.name}</div>
                            </td>
                          )}
                          {isColVisible('address') && (
                            <td className="px-4 py-3 text-sm text-gray-700">
                              <div className="truncate" title={row.address}>{row.address}</div>
                            </td>
                          )}
                          {isColVisible('welfareStatus') && (
                            <td className="px-4 py-3">
                              {row.welfareStatus ? (
                                <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-1 text-xs ${welfareColor(row.welfareStatus)}`}>
                                  {row.welfareStatus}
                                </span>
                              ) : (
                                '—'
                              )}
                            </td>
                          )}
                          {isColVisible('bankInfo') && (
                            <td className="px-4 py-3 text-center text-sm">
                              {row.bankInfoFilled ? (
                                <span className="font-medium text-emerald-700">有</span>
                              ) : (
                                <span className="text-gray-500">無</span>
                              )}
                            </td>
                          )}
                          {isColVisible('amount') && (
                            <td className="whitespace-nowrap px-4 py-3 text-sm">{(() => {
                              const key = caseStatusKeyByLabel(currentPaymentState)
                              const calculated = key != null && CASE_STATUS_CODE_MAP[key] >= CASE_STATUS_CODE_MAP.CALC_DONE
                              if (!calculated || amount == null) return 'ー'
                              return `${amount.toLocaleString()}円`
                            })()}</td>
                          )}
                          {isColVisible('pStatus') && (
                            <td className="px-4 py-3">
                              <DemoBadge status={currentPaymentState} />
                            </td>
                          )}
                          {isColVisible('officeReviewStatus') && (
                            <td className="px-4 py-3">
                              <OfficeReviewStatusTag statusId={row.officeReviewStatusId} />
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            total={total}
            rangeFrom={rangeFrom}
            rangeTo={rangeTo}
            onPageChange={(next) => { setLoading(true); setPage(next); setSavedSearch({ ...applied, page: next }); }}
            pageSize={pageSize}
            onPageSizeChange={(next) => { setLoading(true); setPageSize(next); setPage(1); setSavedSearch({ ...applied, page: 1 }); }}
            className="mt-5"
          />
        </ResultCard>
      </main>

      <Modal
        open={modalOpen}
        title={isEdit ? '給付額算出対象更新' : '給付額算出対象作成'}
        onCancel={closeCompletionModal}
        onOk={() => void handleConfirmSave()}
        confirmLoading={saving}
        okText={isEdit ? '更新' : '作成'}
        cancelText="キャンセル"
        width={720}
        mask={{ closable: !saving }}
        destroyOnHidden
      >
        <p className="mb-4 text-sm text-gray-600">
          {isEdit
            ? 'リスト名を入力してください。振込指定日・決定通知書文書番号・決定通知書発行日・追加給付の支給日及び支給方法は作成時に設定済みのため変更できません。'
            : 'リスト名と振込指定日を入力してください。'}
        </p>
        <Form
          form={completionForm}
          layout="vertical"
          className="customer-validation-form"
          initialValues={{
            jobName: jobNameValue,
            transferDate: transferDateValue,
            decisionNoticeDocumentNo: decisionNoticeDocumentNoValue,
            decisionNoticeIssueDate: decisionNoticeIssueDateValue,
            decisionNoticePaymentDateMethod: decisionNoticePaymentDateMethodValue,
          }}
        >
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
            <Form.Item
              name="jobName"
              label="リスト名"
              rules={[
                { required: true, whitespace: true, message: 'リスト名を入力してください。' },
                { max: PAYMENT_JOB_NAME_MAX_LENGTH, message: `リスト名は${PAYMENT_JOB_NAME_MAX_LENGTH}文字以内で入力してください。` },
              ]}
            >
              <Input placeholder="例: 4月度 追加給付振込" autoComplete="off" maxLength={PAYMENT_JOB_NAME_MAX_LENGTH} showCount />
            </Form.Item>
            <Form.Item
              name="transferDate"
              label="振込指定日"
              rules={[{ required: true, message: '振込指定日を入力してください。' }]}
              extra={isEdit ? '作成時のみ設定できます（編集では変更不可）。' : undefined}
            >
              <Input type="date" min="1900-01-01" max="9999-12-31" disabled={isEdit} />
            </Form.Item>
            <Form.Item
              name="decisionNoticeDocumentNo"
              label="決定通知書文書番号"
              extra={isEdit ? '作成時のみ設定できます（編集では変更不可）。' : undefined}
            >
              <Input placeholder="例: 第2026-001号" autoComplete="off" allowClear={!isEdit} disabled={isEdit} />
            </Form.Item>
            <Form.Item
              name="decisionNoticeIssueDate"
              label="決定通知書発行日"
              extra={
                isEdit
                  ? '作成時のみ設定できます（編集では変更不可）。'
                  : '未入力の場合は算出実行日が決定通知書に印字されます。'
              }
            >
              <Input type="date" min="1900-01-01" max="9999-12-31" allowClear={!isEdit} disabled={isEdit} />
            </Form.Item>
          </div>
          <Form.Item
            name="decisionNoticePaymentDateMethod"
            label="追加給付の支給日及び支給方法"
            extra={
              isEdit
                ? '作成時のみ設定できます（編集では変更不可）。'
                : '入力した内容を決定通知書に印字します。未入力の場合は、口座情報と振込指定日から自動で決めます。'
            }
          >
            <Input
              placeholder="窓口にて支給"
              autoComplete="off"
              allowClear={!isEdit}
              disabled={isEdit}
            />
          </Form.Item>
        </Form>
      </Modal>

      <BankInfoIssueDialog
        open={bankIssueDialogOpen}
        title="受給者口座情報に不備があります"
        description={
          '選択した受給者の口座情報に不備があるため、給付額算出対象の作成を中止しました。\n下記の不備を解消してから、もう一度「作成」してください。'
        }
        issues={bankIssues}
        onClose={() => setBankIssueDialogOpen(false)}
      />
    </div>
  )
}

export default PaymentJobCreate
